import {useAtomValue} from 'jotai';
import {observer} from 'mobx-react-lite';
import React, {useMemo} from 'react';
import {useLocation} from 'react-router-dom';

import {Logo} from '_/assets/images';
import {usePermissionChecker} from '_/hooks/usePermissionChecker';
import {pendingEmailState} from '_/state/pendingEmail';
import {ModuleInfo} from '_/utilities/types';

import {
  MainItemsList,
  SecondaryItemsList,
  StyledNavigationBar,
} from './NavigationBar.styles';
import {NavigationBarItem} from './NavigationBarItem';

type Props = {
  activeTabId?: string;
  secondaryTabs: ModuleInfo[];
  tabs: ModuleInfo[];
};

const UNavigationBar = ({
  activeTabId,
  secondaryTabs,
  tabs,
}: Props): React.ReactElement => {
  const hasPendingEmail = useAtomValue(pendingEmailState);
  const {pathname} = useLocation();
  const {checkOnePermission} = usePermissionChecker();

  const showEmailNotification = useMemo(
    () => hasPendingEmail && !pathname.startsWith('/communication'),
    [hasPendingEmail, pathname]
  );

  const filteredTabs = tabs.filter(
    (item) =>
      !item.requiresOneOfPermissions ||
      checkOnePermission(item.requiresOneOfPermissions)
  );

  const filteredSecondaryTabs = secondaryTabs.filter(
    (item) =>
      !item.requiresOneOfPermissions ||
      checkOnePermission(item.requiresOneOfPermissions)
  );

  return (
    <StyledNavigationBar>
      <div className='logo-wrapper'>
        <Logo data-testid='Icon-Logo' />
      </div>
      <MainItemsList role='tablist'>
        {filteredTabs.map(
          (tabInfo): React.ReactNode => (
            <NavigationBarItem
              key={tabInfo.id}
              active={tabInfo.id === activeTabId}
              hasNotification={
                tabInfo.id === 'communication' && showEmailNotification
              }
              Icon={tabInfo.Icon}
              module={tabInfo.module}
              modulePath={tabInfo.modulePath}
              text={tabInfo.text}
            />
          )
        )}
      </MainItemsList>
      {filteredSecondaryTabs.length > 0 ? (
        <SecondaryItemsList role='tablist'>
          {filteredSecondaryTabs.map(
            (tabInfo): React.ReactNode => (
              <NavigationBarItem
                key={tabInfo.id}
                active={tabInfo.id === activeTabId}
                Icon={tabInfo.Icon}
                module={tabInfo.module}
                modulePath={tabInfo.modulePath}
                text={tabInfo.text}
              />
            )
          )}
        </SecondaryItemsList>
      ) : null}
    </StyledNavigationBar>
  );
};

export const NavigationBar = observer(UNavigationBar);
